import { useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import { Button, DatePicker, Input, Radio, Select } from "@/components";
import { NumberString } from "@/components/NumberString";
import { PostalCodeInput } from "@/components/PostalCodeInput";
import { usePerfectureStore } from "@/store/perfectureStore";
import { ApiErrorResponse, ECOMPANY_BUSINESS, ECOMPANY_SERIES } from "@/types";
import { cn } from "@/utils";
import { toHalfWidth } from "@/utils/formatCodeNumber";
import { zodResolver } from "@hookform/resolvers/zod";
import { schema, schemaEdit } from "./schema";
import { CreateCompanyFormData, EditCompanyFormData, Role } from "./type";

const SERIES_OPTIONS = Object.entries(ECOMPANY_SERIES).map(([_, value]) => ({
  label: value,
  value: value,
}));

const BUSINESS_OPTIONS = Object.entries(ECOMPANY_BUSINESS).map(
  ([_, value]) => ({
    label: value,
    value: value,
  })
);

export const CompanyForm = ({
  type = "create",
  defaultValues,
  serverError,
  isLoading,
  restrictFields = false,
  onSubmit,
  onCancel,
}: {
  type?: "create" | "edit";
  defaultValues?: Partial<CreateCompanyFormData | EditCompanyFormData>;
  serverError?: ApiErrorResponse | null;
  isLoading?: boolean;
  restrictFields?: boolean;
  onSubmit: (data: CreateCompanyFormData | EditCompanyFormData) => void;
  onCancel: () => void;
}) => {
  const companyCodeRef = useRef<HTMLInputElement>(null);
  const corporateCodeRef = useRef<HTMLInputElement>(null);
  const companyEmailRef = useRef<HTMLInputElement>(null);
  const companyUserEmailRef = useRef<HTMLInputElement>(null);
  const joiningDateRef = useRef<HTMLInputElement>(null);
  const { prefectures } = usePerfectureStore();
  const isRestrictedDetailRole = type === "edit" && restrictFields;
  const restrictedClass = isRestrictedDetailRole ? "hidden" : "";
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    control,
    setError,
    clearErrors,
    trigger,
  } = useForm<CreateCompanyFormData | EditCompanyFormData>({
    defaultValues: defaultValues as any,
    shouldUnregister: false,
    resolver: zodResolver(type === "create" ? schema : schemaEdit),
  });

  useEffect(() => {
    if (!serverError) return;
    if (serverError.error === "corporate_code") {
      setError("companyData.corporateCode", {
        type: "server",
        message: serverError.message,
      });
      corporateCodeRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      corporateCodeRef.current?.focus();
    }
    if (serverError.error === "business_code") {
      setError("companyData.companyCode", {
        type: "server",
        message: serverError.message,
      });
      companyCodeRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      companyCodeRef.current?.focus();
    }
    if (serverError.error === "business_user.email") {
      setError("companyData.companyUser.email", {
        type: "server",
        message: serverError.message,
      });
      companyUserEmailRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      companyUserEmailRef.current?.focus();
    }
    if (serverError.error === "email") {
      setError("companyData.email", {
        type: "server",
        message: serverError.message,
      });
      companyEmailRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      companyEmailRef.current?.focus();
    }
    if (serverError.error === "joining_date") {
      setError("companyData.joiningDate", {
        type: "server",
        message: serverError.message,
      });
      joiningDateRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      joiningDateRef.current?.focus();
    }
    if (serverError.error === null) {
      clearErrors("companyData.companyCode");
      clearErrors("companyData.corporateCode");
      clearErrors("companyData.companyUser.email");
      clearErrors("companyData.email");
      clearErrors("companyData.joiningDate");
    }
  }, [serverError]);

  const clearServerError = async () => {
    if (serverError) {
      clearErrors("companyData");
      await trigger();
    }
  };

  // Watch role để theo dõi thay đổi
  const currentRole = watch("role");
  const currentJoiningDate = watch("companyData.joiningDate");
  const currentUnsubscribingDate = watch("companyData.unSubscribingDate");
  const memberClass = currentRole === Role.MEMBER ? "" : "hidden";

  useEffect(() => {
    // Vì companyCode chỉ có ở member + required nên cần clear error khi role thay đổi
    clearServerError();
    if ((errors.companyData as any)?.companyCode) {
      clearErrors("companyData.companyCode");
    }
  }, [currentRole]);

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <LabelSection className={`${type === "edit" ? "opacity-20" : ""} ${restrictedClass}`}>
        事業所区分
      </LabelSection>
      <Controller
        name={"role"}
        control={control}
        render={({ field: { onChange, value } }) => (
          <div
            className={`grid grid-cols-2 gap-5 text-sm leading-[1.375rem] relative ${restrictedClass}`}
          >
            <label className="flex items-center gap-2">
              <Radio
                value={Role.MEMBER}
                checked={value === Role.MEMBER}
                onChange={onChange}
                className="w-5 h-5"
              />
              会員
            </label>
            <label className="flex items-center gap-2">
              <Radio
                value={Role.NON_MEMBER}
                checked={value === Role.NON_MEMBER}
                onChange={onChange}
                className="w-5 h-5"
              />
              非会員
            </label>
            <div
              className={cn(
                "absolute top-0 left-0 bg-white opacity-80 z-100 w-full h-full",
                type === "create" && "hidden"
              )}
            />
          </div>
        )}
      />

      <LabelSection>基本情報</LabelSection>
      <div className="grid grid-cols-2 gap-4">
        {/* Chỉ hiện khi role là "member" */}
        <Controller
          control={control}
          name="companyData.companyCode"
          render={({ field: { onChange, value }, fieldState }) => (
            <NumberString
              ref={companyCodeRef}
              disabled={type === "edit"}
              label="事業所コード"
              name="companyData.companyCode"
              placeholder="事業所コードを入力してください。"
              maxLength={6}
              required
              error={fieldState.error?.message}
              value={value || ""}
              containerClassName={restrictedClass}
              onChange={(e) => {
                onChange(e);
                if (
                  (errors.companyData as any)?.companyCode?.type === "server"
                ) {
                  clearServerError();
                }
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="companyData.corporateCode"
          render={({ field: { onChange, value } }) => (
            <NumberString
              label="法人コード"
              name="companyData.corporateCode"
              placeholder="13桁の数字を入力してください。"
              maxLength={13}
              error={(errors.companyData as any)?.corporateCode?.message}
              ref={corporateCodeRef}
              value={value || ""}
              containerClassName={restrictedClass}
              onChange={(e) => {
                onChange(e);
                if (
                  (errors.companyData as any)?.corporateCode?.type === "server"
                ) {
                  clearServerError();
                }
              }}
            />
          )}
        />

        <Input
          label="法人名"
          placeholder="法人名を入力してください。"
          required
          containerClassName={`col-span-2 ${restrictedClass}`}
          error={errors.companyData?.corporateName?.message}
          {...register("companyData.corporateName")}
        />
        <Controller
          control={control}
          name="companyData.series"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Select
              label="系列"
              required
              value={
                value
                  ? SERIES_OPTIONS.find((option) => option.value === value)
                  : undefined
              }
              onChange={(data: any) => {
                onChange(data?.value);
              }}
              options={SERIES_OPTIONS}
              className={cn(memberClass, restrictedClass)}
              error={error?.message ?? ""}
            />
          )}
        />
        <Controller
          control={control}
          name="companyData.business"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Select
              label="商態"
              required
              isDisabled={type === "edit"}
              value={
                value
                  ? BUSINESS_OPTIONS.find((option) => option.value === value)
                  : undefined
              }
              onChange={(data: any) => {
                onChange(data?.value);
              }}
              options={BUSINESS_OPTIONS}
              className={cn(memberClass, restrictedClass)}
              error={error?.message ?? ""}
            />
          )}
        />
        <Input
          label="代表者名"
          placeholder="代表者の名称を入力してください。"
          containerClassName={` col-span-2 ${memberClass} ${restrictedClass}`}
          error={(errors.companyData as any)?.representativePosition?.message}
          {...register("companyData.representativePosition" as any)}
        />
        <Controller
          control={control}
          name="companyData.postCode"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <div className={restrictedClass}>
              <PostalCodeInput
                label="郵便番号"
                name="companyData.postCode"
                placeholder="〒000-0000"
                maxLength={8}
                error={error?.message || ""}
                value={value || ""}
                onChange={onChange}
              />
            </div>
          )}
        />
        <Controller
          control={control}
          name="companyData.prefecture"
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            const prefecture = value
              ? prefectures.find((option) => option.value === +value)
              : undefined;
            return (
              <Select
                label="都道府県"
                placeholder="内容を選択してください。"
                isClearable
                value={prefecture}
                onChange={(data: any) => {
                  onChange(data?.value || null);
                }}
                options={prefectures}
                className={restrictedClass}
                error={error?.message ?? ""}
              />
            );
          }}
        />
        <Input
          label="住所"
          required
          placeholder="住所を入力してください。"
          containerClassName={`col-span-2 ${restrictedClass}`}
          error={errors.companyData?.address?.message}
          {...register("companyData.address")}
        />
        <Controller
          control={control}
          name="companyData.phoneNumber"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <NumberString
              label="電話番号"
              name="companyData.phoneNumber"
              placeholder="電話番号を入力してください。"
              containerClassName={`col-span-2 ${restrictedClass}`}
              error={error?.message ?? ""}
              value={value || ""}
              onChange={onChange}
              maxLength={255}
              allowHyphen
            />
          )}
        />
        <Controller
          control={control}
          name="companyData.email"
          render={({ field: { onChange, value } }) => (
            <Input
              ref={companyEmailRef}
              label="メールアドレス"
              name="companyData.email"
              placeholder="メールアドレスを入力してください。"
              containerClassName={`col-span-2 ${restrictedClass}`}
              error={errors.companyData?.email?.message}
              value={value || ""}
              maxLength={150}
              onChange={(e) => {
                onChange(e.target.value);
                if ((errors.companyData as any)?.email?.type === "server") {
                  clearServerError();
                }
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="companyData.joiningDate"
          render={({ field: { onChange, value } }) => (
            <DatePicker
              label="入会日"
              ref={joiningDateRef}
              value={value as Date | null | undefined}
              onChange={onChange}
              containerClassName={cn(memberClass, restrictedClass)}
              error={(errors.companyData as any)?.joiningDate?.message}
              maxDate={
                currentUnsubscribingDate
                  ? dayjs(new Date(currentUnsubscribingDate))
                      .subtract(1, "day")
                      .toDate()
                  : undefined
              }
            />
          )}
        />
        <Controller
          control={control}
          name="companyData.unSubscribingDate"
          render={({ field: { onChange, value } }) => (
            <DatePicker
              label="退会日"
              value={value as Date | null | undefined}
              onChange={onChange}
              containerClassName={cn(memberClass, restrictedClass)}
              minDate={
                currentJoiningDate
                  ? dayjs(new Date(currentJoiningDate)).add(1, "day").toDate()
                  : undefined
              }
            />
          )}
        />
        <Input
          label="店舗表示名称"
          placeholder="店舗表示名称を入力してください。"
          containerClassName={`col-span-2 ${isRestrictedDetailRole ? "" : memberClass}`}
          error={(errors.companyData as any)?.storeName?.message}
          {...register("companyData.storeName" as any)}
        />
        <Input
          label="メモ欄"
          placeholder="ご自由にご記入ください。"
          {...register("companyData.notes")}
          containerClassName={`col-span-2 ${restrictedClass}`}
        />
      </div>

      <LabelSection className={`${type === "edit" ? "hidden" : ""}`}>
        事業所ユーザー
      </LabelSection>
      <div
        className={`grid grid-cols-2 gap-4 ${type === "edit" ? "hidden" : ""}`}
      >
        <Input
          label="役割"
          value="企業管理者"
          disabled
          {...register("companyData.companyUser.userRole")}
          containerClassName={`col-span-2 ${memberClass}`}
        />
        <Input
          label="ユーザー名"
          placeholder="ユーザー名を入力してください。"
          required={type === "create"}
          containerClassName="col-span-2"
          maxLength={150}
          error={(errors.companyData as any)?.companyUser?.userName?.message}
          {...register("companyData.companyUser.userName")}
        />
        <Controller
          control={control}
          name="companyData.companyUser.phoneNumber"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <NumberString
              label="電話番号"
              name="companyData.companyUser.phoneNumber"
              placeholder="電話番号を入力してください。"
              containerClassName="col-span-2"
              error={error?.message ?? ""}
              value={value || ""}
              onChange={onChange}
              maxLength={255}
              allowHyphen
            />
          )}
        />
        <Input
          label="営業所"
          placeholder="営業所を入力してください。"
          {...register("companyData.companyUser.salesOffice")}
          containerClassName={`col-span-2 ${memberClass}`}
        />
        <Input
          label="役職"
          placeholder="役職を入力してください。"
          {...register("companyData.companyUser.post")}
          containerClassName={memberClass}
        />
        <Input
          label="部署"
          placeholder="部署を入力してください。"
          {...register("companyData.companyUser.department")}
          containerClassName={memberClass}
        />
        <Controller
          control={control}
          name="companyData.companyUser.email"
          render={({ field: { onChange, value } }) => (
            <Input
              ref={companyUserEmailRef}
              label="メールアドレス"
              name="companyData.companyUser.email"
              placeholder="メールアドレスを入力してください。"
              required={type === "create"}
              containerClassName="col-span-2"
              error={(errors.companyData as any)?.companyUser?.email?.message}
              value={value || ""}
              maxLength={150}
              onChange={(e) => {
                onChange(e.target.value);
                if (
                  (errors.companyData as any)?.companyUser?.email?.type ===
                  "server"
                ) {
                  clearServerError();
                }
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="companyData.companyUser.password"
          render={({ field: { onChange, value } }) => (
            <Input
              label="パスワード"
              name="companyData.companyUser.password"
              placeholder="最低8文字、かつ少なくとも1文字のアルファベットを含む"
              required={type === "create"}
              containerClassName="col-span-2"
              error={
                (errors.companyData as any)?.companyUser?.password?.message
              }
              value={value || ""}
              maxLength={150}
              onChange={onChange}
              onBlur={() => {
                onChange(toHalfWidth(value));
              }}
            />
          )}
        />
      </div>

      {/* button  */}
      <div className="h-16" />
      <div className="flex w-[calc(100%-1.5rem)] justify-end gap-4 bg-white absolute bottom-0 py-4 z-10 -ml-2 pr-2">
        <Button
          buttonType="text"
          className="!h-12"
          type="button"
          onClick={onCancel}
        >
          キャンセル
        </Button>
        <Button
          buttonType="primary"
          className="!h-12"
          type="submit"
          disabled={!isEmpty(errors)}
          isLoading={isLoading}
        >
          {type === "edit" ? "更新する" : "作成"}
        </Button>
      </div>
    </form>
  );
};

const LabelSection = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <p className={`text-base font-semibold leading-6 ${className}`}>
      {children}
    </p>
  );
};
